import React, { useState, useMemo } from 'react';
import Loader from '../../common/Loader';
import PageTitle from '../../components/PageTitle';
import { Box, Card, CardContent, Paper, Typography } from '@mui/material';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { FormProvider, RHFSearchableSelectField, RHFTextField } from '../../common/hook-form';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { apiConstants, postGeneric } from '../../common/constants';
import _ from 'lodash';
import { formatDate, calcPercentage } from '../../helpers/UtilityHelper';
import MUIModal from '../../components/MuiModal';
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls';
import { FieldLabel, FilterField } from '../../common/FilterBar';
import { statusBadgeSx, kpiCardSx, kpiRowSx, filterButtonSx, accentColor } from '../../common/sharedStyles';
import palette from '../../../theme/palette';
import CopyIcon from '../../common/CopyIcon';

// ─── Cell helpers ─────────────────────────────────────────────────────────────

const CellLine = ({ children, muted, bold }) => (
    <Box sx={{ fontSize: 12.5, lineHeight: 1.4, color: muted ? 'text.secondary' : 'text.primary', fontWeight: bold ? 600 : 400 }}>
        {children}
    </Box>
);

function OutcomeBadge({ value }) {
    const ok = value === true;
    return (
        <Box component="span" sx={{
            ...statusBadgeSx(ok ? 'success' : 'error'),
            textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '10px',
        }}>
            {ok ? 'Connected' : 'Not Connected'}
        </Box>
    );
}

// Voicemail pickups carry talk-time (~1 min) but nobody actually answered —
// business-wise that is NOT a connected call, so the NDR result overrides the
// duration signal for both the Outcome badge and the Connected KPI.
const isVoicemail = (row) => (row?.ndr_result || '').toLowerCase().includes('voicemail');
const isConnectedCall = (row) => Number(row?.call_duration) > 0 && !isVoicemail(row);

// call_duration is stored in MINUTES (e.g. 3 = 3 min). Format accordingly.
function formatCallDuration(minutes) {
    if (minutes == null || minutes === '—') return '—';
    const v = Number(minutes);
    if (isNaN(v)) return '—';
    const m = Math.floor(v);
    const sec = Math.round((v - m) * 60);
    if (m > 0 && sec > 0) return `${m}m ${sec}s`;
    if (m > 0) return `${m}m`;
    return `${sec}s`;
}

// ─── KPI helpers ──────────────────────────────────────────────────────────────

function CallKPIRow({ rows }) {
    const kpis = useMemo(() => {
        if (!rows || !rows.length) return null;
        const total = rows.length;
        const connected = rows.filter(isConnectedCall).length;
        const connectRate = total > 0 ? calcPercentage(connected, total) : 0;
        const durations = rows.map(r => Number(r.call_duration)).filter(d => !isNaN(d) && d > 0);
        const avgDuration = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;
        const reattemptCount = rows.filter(r => (r.ndr_result || '').toLowerCase().includes('reattempt')).length;
        const rtoCount = rows.filter(r => (r.ndr_result || '').toLowerCase().includes('cancel')).length;
        return { total, connected, connectRate, avgDuration, reattemptCount, rtoCount };
    }, [rows]);

    if (!kpis) return null;

    const cards = [
        { label: 'Total Calls', value: kpis.total, color: 'primary' },
        { label: 'Connected', value: `${kpis.connected} (${kpis.connectRate}%)`, color: 'success' },
        { label: 'Avg Duration', value: formatCallDuration(kpis.avgDuration), color: 'info' },
        { label: 'Reattempt', value: kpis.reattemptCount, color: 'warning' },
        { label: 'Cancel / RTO', value: kpis.rtoCount, color: 'error' },
    ];

    return (
        <Box sx={kpiRowSx}>
            {cards.map(c => (
                <Paper key={c.label} sx={kpiCardSx(c.color)}>
                    <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem' }}>
                        {c.label}
                    </Typography>
                    <Typography variant='h5' sx={{ mt: 0.5, fontWeight: 800, color: accentColor(c.color) }}>
                        {c.value}
                    </Typography>
                </Paper>
            ))}
        </Box>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────

const NDRCall = () => {
    const [page, setPage] = useState(0);
    const [filterChange, setFiltersChange] = useState(0);
    const [filters, setFilters] = useState();
    const [data, setData] = useState();
    const [sizePerPage, setSizePerPage] = useState(20);
    const [loading, setLoading] = useState(false);
    const [ndrQ, setNdrQ] = useState([]);
    const [ndrQModal, setNdrQModal] = useState(false);
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery();

    const methods = useForm({
        defaultValues: { awbnumber: '', ndrResults: '', merchant: '' }
    });
    const { handleSubmit, reset } = methods;

    const NDRResults = ["Reattempt Delivery", "Cancel Delivery", "Delivered", "Wrong Number", "Not Connected",
        "Reattempt Delivery Address Updated", "Busy or Improper Response", "Language Barrier", "Redial"];
    const options = NDRResults.map((op) => ({ label: _.startCase(op), value: op }));

    const handleFilterSubmit = (param) => {
        setFiltersChange(filterChange + 1);
        setFilters(param);
    };

    useEffect(() => { setPage(0); }, [sizePerPage, filters]);

    useEffect(() => {
        setLoading(true);
        const filtersToApply = [];
        if (filters && (filters.awbnumber || filters.ndrResults || filters.merchant)) {
            if (filters.awbnumber) filtersToApply.push({ field: "awb_number", operator: "eq", value: filters.awbnumber });
            if (filters.ndrResults) filtersToApply.push({ field: "ndr_result", operator: "eq", value: filters.ndrResults });
            if (filters.merchant) filtersToApply.push({ field: "merchantOid", operator: "eq", value: filters.merchant });
        }
        const payload = {
            "offset": page * sizePerPage, "limit": sizePerPage,
            "skipCount": true, "countOnly": false,
            "sort": { "direction": "DESC", "field": "created_at" },
            "andfilter": filtersToApply
        };
        postGeneric(apiConstants.NDR_RECORDING_SEARCH, payload).then((res) => {
            setLoading(false);
            setData(res?.data?.data);
        }).catch((err) => { console.log(err); setLoading(false); });
    }, [sizePerPage, page, filterChange, filters]);

    const columns = [
        {
            field: 'awbNumber',
            headerName: 'AWB No.', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 140,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <CellLine bold>{row?.awb_number || '—'}</CellLine>
                    <CopyIcon text={row?.awb_number} />
                </Box>
            ),
        },
        {
            field: 'merchantOid',
            headerName: 'Merchant', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, 
            renderCell: ({ row }) => {
                const merchant = merchants?.find((m) => m.id === row?.merchantOid);
                return <CellLine>{merchant ? merchant.name : '—'}</CellLine>;
            },
        },
        {
            field: 'ndrresults',
            headerName: 'NDR Result', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 150, 
            renderCell: ({ row }) => <CellLine>{row.ndr_result || '—'}</CellLine>,
        },
        {
            field: 'startTime',
            headerName: 'Call Time', headerAlign: 'left', align: 'left',
            hideSortIcons: true, minWidth: 200, 
            renderCell: ({ row }) => <CellLine muted>{formatDate(row.call_start_time)}</CellLine>,
        },
        {
            field: 'callDuration',
            headerName: 'Duration', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 110,
            renderCell: ({ row }) => (
                <Box sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>{formatCallDuration(row?.call_duration)}</Box>
            ),
        },
        {
            field: 'ndr_outcome',
            headerName: 'Outcome', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 100,
            // A call with talk-time (duration > 0) was connected — EXCEPT voicemail:
            // the recording has ~1 min of duration but nobody answered, so a
            // voicemail ndr_result always shows Not Connected.
            renderCell: ({ row }) => <OutcomeBadge value={isConnectedCall(row)} />,
        },
        {
            field: 'recordingURL',
            headerName: 'Recording', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 100,
            renderCell: ({ row }) => {
                if (!row.recording_url) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                return (
                    <Box
                        component="a"
                        href={row.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px',
                            borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                            color: palette.primary.main, border: `1px solid ${palette.primary.lighter}`, textDecoration: 'none',
                            bgcolor: 'transparent', transition: 'all 0.15s',
                            '&:hover': { bgcolor: palette.info.lighter, borderColor: palette.info.light },
                        }}
                    >
                        Play
                    </Box>
                );
            },
        },
        {
            field: 'call_responses',
            headerName: 'Call Response', headerAlign: 'center', align: 'center',
            hideSortIcons: true, width: 130,
            renderCell: ({ row }) => {
                if (!row?.call_responses?.length) return <Box sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Box>;
                return (
                    <Box
                        component="button"
                        onClick={() => { setNdrQ(row.call_responses); setNdrQModal(true); }}
                        sx={{
                            display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px',
                            borderRadius: '4px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            bgcolor: 'transparent', color: palette.primary.main, border: `1px solid ${palette.primary.lighter}`,
                            transition: 'all 0.15s', '&:hover': { bgcolor: palette.info.lighter, borderColor: palette.info.light },
                        }}
                    >
                        View Q&A
                    </Box>
                );
            },
        },
    ];

    return (
        <>
            <PageTitle>Call Recordings</PageTitle>
            {loading && <Loader />}

            {/* ── KPI Cards ────────────────────────────────────────────── */}
            <CallKPIRow rows={data} />

            {/* ── Filter bar ────────────────────────────────────────────── */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(handleFilterSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <RHFSearchableSelectField
                                    width="100%"
                                    name="merchant"
                                    label=""
                                    options={_.orderBy(merchants, "name").map((val) => ({ value: val.id, label: val.name }))}
                                />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>AWB Number</FieldLabel>
                                <RHFTextField name="awbnumber" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>NDR Result</FieldLabel>
                                <RHFSearchableSelectField name="ndrResults" label="" width="100%" options={_.orderBy(options, 'value')} />
                            </FilterField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <Box component="button" type="submit"
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', ...filterButtonSx('apply') }}>
                                    Filter
                                </Box>
                                <Box component="button" type="button"
                                    onClick={() => { reset({ awbnumber: '', ndrResults: '' }); setData(); }}
                                    sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', ...filterButtonSx('reset') }}>
                                    Reset
                                </Box>
                            </Box>

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Data table ────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: palette.background.paper, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    rows={data ? data.map((row, index) => ({ ...row, id: index + 1 })) : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    rowCount={Number.MAX_VALUE}
                    hideCountNumber={true}
                    filterMode={"server"}
                    getRowHeight={() => 'auto'}
                    getRowId={(row) => row.id}
                />
            </Box>

            <MUIModal open={ndrQModal} title="Call Details" maxWidth={"lg"} handleClose={() => setNdrQModal(false)}>
                <ProzoDataGrid
                    columns={[
                        { field: "question", headerName: "Question", flex: 3 },
                        { field: "answer", flex: 1, headerName: "Answer" },
                    ]}
                    autoHeight={true}
                    rows={ndrQ || []}
                    pagination={false}
                    hideFooterPagination={true}
                    getRowId={(row) => Math.random()}
                />
            </MUIModal>
        </>
    );
};

export default NDRCall;
